import React, { useEffect, useRef, useState } from 'react';
import { AvatarUpload, Button, Input, SparkIcon, Spinner, sparkToast, useReadOnly } from '@spark';

import SettingsSectionCard from '@/features/settings/components/SettingsSectionCard.jsx';
import { useAuth } from '@/features/auth/AuthContext';
import { updateProfile } from '@/features/settings/api/accountApi';
import { LOGO_ACCEPT, cropImageToSquare, logoImageErrorText } from '@/features/workspaces/utils/logoImage';
import useLogoCrop from '@/features/workspaces/hooks/useLogoCrop';
import LogoCropModal from '@/features/workspaces/components/LogoCropModal';
import DeleteAccountModal from './DeleteAccountModal.jsx';
import './settings.css';
import './ProfileInformation.css';

const NAME_MAX_LENGTH = 200;
const DANGER_ZONE_TITLE = 'Danger Zone';
const SAVING_LABEL = 'Saving…';

/**
 * "Saving…" shown to the right of a profile control while its autosave is in
 * flight. The element always occupies its space (hidden, not removed), so
 * the control never shifts or blinks when the status appears and disappears.
 */
const SavingStatus = ({ active, controlId }) => (
    <span
        id={`${controlId}-saving`}
        className={`profile-settings__saving${active ? ' profile-settings__saving--active' : ''}`}
        aria-hidden={active ? undefined : true}
    >
        <Spinner size="small" aria-label={SAVING_LABEL} />
        <span className="fs-unmask" aria-hidden="true">
            {SAVING_LABEL}
        </span>
    </span>
);

/**
 * Settings > Profile (Figma 9396:5032): the signed-in user's picture, full
 * name and (read-only) email, then a Danger Zone with "Delete Account".
 *
 * Picture and name autosave through ``POST /users/set-profile`` — the picture
 * once it has been framed in `LogoCropModal`, the name on blur / Enter when
 * it is non-blank and differs from the saved value — and the session user is
 * patched in place so the avatar menu refreshes without a reload.  Escape
 * restores the saved name.  Everything is disabled when authentication is
 * off (there is no account to edit) or the platform is read-only.
 *
 * "Delete Account" opens `DeleteAccountModal`, which owns the rest of the
 * flow (ownership hand-over stepper, ``DELETE /users/me``, success view).
 *
 * `cropPicture` is injectable so tests can skip the canvas pipeline.
 */
const ProfileInformation = ({ cropPicture = cropImageToSquare }) => {
    const { user, isAuthEnabled, applyUserUpdate } = useAuth();
    const { readOnly } = useReadOnly();
    const disabled = readOnly || !isAuthEnabled || !user?.email;

    const savedName = user?.name || '';
    const [name, setName] = useState(savedName);
    const [savingName, setSavingName] = useState(false);
    const [savingPicture, setSavingPicture] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    // What the server holds as far as this component knows: the session name,
    // or the name of a save that succeeded and has not reached the session yet.
    const lastSavedName = useRef(savedName);

    // Follow the session (save confirmed, another tab renamed) — but never
    // over a draft the user is still typing.
    useEffect(() => {
        const previous = lastSavedName.current;
        lastSavedName.current = savedName;
        setName((draft) => (draft.trim() === previous || draft.trim() === savedName ? savedName : draft));
    }, [savedName]);

    const saveName = async () => {
        if (disabled || savingName) return;
        const trimmed = name.trim();
        if (!trimmed) {
            // Blank is never saved; snap back to what the server has.
            setName(lastSavedName.current);
            return;
        }
        if (trimmed === lastSavedName.current) {
            if (trimmed !== name) setName(trimmed);
            return;
        }
        setSavingName(true);
        try {
            const result = await updateProfile({ name: trimmed });
            if (!result.ok) {
                sparkToast.error(result.error);
                return;
            }
            lastSavedName.current = trimmed;
            applyUserUpdate({ name: trimmed, name_set_by_user: true });
            sparkToast.success('Name updated');
        } finally {
            setSavingName(false);
        }
    };

    const handleNameKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            saveName();
        } else if (event.key === 'Escape') {
            setName(lastSavedName.current);
        }
    };

    const savePicture = async (picture) => {
        if (disabled || !picture) return;
        setSavingPicture(true);
        try {
            const result = await updateProfile({ picture });
            if (!result.ok) {
                sparkToast.error(result.error);
                return;
            }
            applyUserUpdate({ picture, picture_set_by_user: true });
            sparkToast.success('Profile picture updated');
        } finally {
            setSavingPicture(false);
        }
    };

    // Picked file → crop dialog → `savePicture(dataUrl)`; an unsupported file
    // or a crop failure toasts and keeps the current picture.
    const pictureCrop = useLogoCrop({
        onLogo: savePicture,
        onError: (err) => sparkToast.error(logoImageErrorText(err)),
    });

    return (
        <SettingsSectionCard iconSlug="user" title="Profile">
            <div className="settings-providers__panel">
                <h3 className="settings-subheading fs-unmask">Manage Your account settings</h3>

                <div className="profile-settings__fields">
                    <div className="profile-settings__field-row">
                        <AvatarUpload
                            label="Your Image"
                            emptyVariant="initials"
                            initials={savedName || user?.email || ''}
                            src={user?.picture || null}
                            alt="Profile picture"
                            accept={LOGO_ACCEPT}
                            disabled={disabled || savingPicture}
                            onFileSelected={pictureCrop.pickLogo}
                        />
                        <SavingStatus active={savingPicture} controlId="profile-settings-picture" />
                    </div>
                    <LogoCropModal {...pictureCrop.modalProps} cropLogo={cropPicture} />

                    <div className="profile-settings__field-row">
                        <Input
                            id="profile-settings-name"
                            className="profile-settings__field"
                            size="medium"
                            label="Full Name"
                            placeholder="Your full name"
                            value={name}
                            maxLength={NAME_MAX_LENGTH}
                            disabled={disabled}
                            aria-busy={savingName || undefined}
                            onChange={(event) => setName(event.target.value)}
                            onBlur={saveName}
                            onKeyDown={handleNameKeyDown}
                            autoComplete="name"
                        />
                        <SavingStatus active={savingName} controlId="profile-settings-name" />
                    </div>

                    <Input
                        id="profile-settings-email"
                        className="profile-settings__field"
                        size="medium"
                        type="email"
                        label="Email"
                        placeholder="Youremail@address.com"
                        value={user?.email || ''}
                        disabled
                        readOnly
                        autoComplete="email"
                    />
                </div>

                <section className="profile-settings__danger-zone" aria-labelledby="profile-danger-zone-title">
                    <h4 id="profile-danger-zone-title" className="profile-settings__danger-heading fs-unmask">
                        {DANGER_ZONE_TITLE}
                    </h4>
                    <Button
                        type="button"
                        variant="secondary"
                        danger
                        size="medium"
                        disabled={disabled}
                        startIcon={<SparkIcon slug="trash" size="medium" />}
                        onClick={() => setDeleteOpen(true)}
                    >
                        Delete Account
                    </Button>
                </section>
            </div>

            <DeleteAccountModal open={deleteOpen} onClose={() => setDeleteOpen(false)} />
        </SettingsSectionCard>
    );
};

export default ProfileInformation;
